import { useState } from "react";
import axios from "axios";
import { X, Sparkles, Copy, Check, MessageCircle, Gift } from "lucide-react";
import { Sparkle, Heart, Star, Flower } from "../doodles/Doodles";
import { WHATSAPP_NUMBER } from "../../constants/data";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hand-lettered thank-you SVG (unique doodle for the loyalty milestone)
const ThankYouDoodle = ({ className = "" }) => (
  <svg viewBox="0 0 220 90" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 65 Q 30 20, 50 40 T 82 45 Q 90 20, 105 45 T 138 45 Q 152 20, 168 50 T 200 40"
          stroke="#7A1F2B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M28 68 L 200 68" stroke="#E08036" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 5"/>
    <circle cx="15" cy="45" r="3" fill="#FF6B9D"/>
    <circle cx="207" cy="45" r="3" fill="#FFD93D"/>
    <path d="M100 78 L 105 82 L 118 74" stroke="#7A1F2B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * LoyaltySparkle — celebratory modal shown once when a user hits 3 paid orders.
 * Props:
 *   userName        — first name for greeting
 *   couponCode      — unique code (e.g. LOYAL-PRIYA-A3F2)
 *   discountPercent — e.g. 10
 *   minOrder        — e.g. 2000
 *   token           — auth bearer token to POST /auth/loyalty/mark-seen
 *   onClose         — parent handler
 */
export default function LoyaltySparkle({ userName, couponCode, discountPercent = 10, minOrder = 2000, token, onClose }) {
  const [copied, setCopied] = useState(false);
  const firstName = (userName || "friend").split(" ")[0];

  const markSeenAndClose = async () => {
    try {
      await axios.post(
        `${API}/auth/loyalty/mark-seen`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
    } catch (_) { /* non-blocking */ }
    onClose();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setCopied(true);
      toast.success("Copied — apply it at checkout!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Please copy the code manually");
    }
  };

  const waMsg = encodeURIComponent(
    `Hi Naathi Charami! 🎁 I just hit my 3rd order milestone as ${firstName}, and I've unlocked my personal loyalty code ${couponCode} for ${discountPercent}% off. Thank you for the sweet surprise 💌`,
  );
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`;

  return (
    <div
      className="fixed inset-0 z-[120] bg-[#2D2424]/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) markSeenAndClose(); }}
      data-testid="loyalty-sparkle-modal"
    >
      <div
        className="relative w-full max-w-md bg-white border-2 border-[#2D2424] rounded-3xl overflow-hidden rotate-1"
        style={{ boxShadow: "8px 8px 0 #E08036" }}
      >
        {/* Doodle decorations */}
        <Sparkle color="#FFD93D" className="absolute top-4 right-16 w-6 animate-twinkle pointer-events-none"/>
        <Sparkle color="#FF6B9D" className="absolute top-14 left-4 w-4 animate-twinkle pointer-events-none" style={{ animationDelay: "0.5s" }}/>
        <Heart color="#FF6B9D" className="absolute bottom-8 left-6 w-5 animate-bounce-doodle pointer-events-none"/>
        <Star color="#BAE1FF" className="absolute top-10 left-14 w-5 animate-spin-slow pointer-events-none"/>
        <Flower color="#FFA94D" className="absolute -bottom-4 -right-4 w-20 rotate-12 opacity-70 pointer-events-none"/>

        <button
          type="button"
          onClick={markSeenAndClose}
          data-testid="loyalty-close"
          aria-label="close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[#2D2424] flex items-center justify-center hover:bg-[#FFE7F1] z-10"
          style={{ boxShadow: "2px 2px 0 #2D2424" }}
        >
          <X className="w-4 h-4"/>
        </button>

        {/* Header — festive celebration gradient */}
        <div
          className="px-6 pt-8 pb-4 text-center"
          style={{ background: "linear-gradient(135deg, #FFF7DC 0%, #FFE7F1 100%)" }}
        >
          <div
            className="mx-auto w-16 h-16 bg-white border-2 border-[#2D2424] rounded-full flex items-center justify-center -rotate-6"
            style={{ boxShadow: "3px 3px 0 #E08036" }}
          >
            <Sparkles className="w-8 h-8 text-[#E08036]" strokeWidth={2}/>
          </div>

          <p className="mt-3 label-small text-[10px]">Loyalty Sparkle · 3 orders unlocked</p>
          <h2 className="font-doodle text-3xl sm:text-4xl text-[#7A1F2B] mt-1 leading-tight" data-testid="loyalty-greeting">
            Thank you, {firstName}! 💌
          </h2>

          {/* Hand-lettered doodle */}
          <ThankYouDoodle className="mx-auto mt-3 w-48 sm:w-56"/>

          <p className="mt-2 font-hand text-sm sm:text-base text-[#5C5555] italic">
            You&apos;ve gifted with us <b>{3} times</b> — you&apos;re officially family.
          </p>
        </div>

        {/* Coupon */}
        <div className="px-6 py-5">
          <div className="border-2 border-dashed border-[#7A1F2B]/60 rounded-2xl p-4 bg-[#FFF6F0]" data-testid="loyalty-perk">
            <div className="flex items-start gap-3">
              <span className="shrink-0 w-9 h-9 rounded-full bg-[#7A1F2B] text-white flex items-center justify-center">
                <Gift className="w-4 h-4"/>
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-doodle text-lg text-[#2D2424] leading-none">Your personal thank-you code</p>
                <p className="font-hand text-xs text-[#5C5555] mt-1">{discountPercent}% off · Minimum order ₹{minOrder.toLocaleString("en-IN")} · Just for you</p>
                <div className="mt-3 flex items-center gap-2">
                  <code
                    className="flex-1 px-3 py-2 border-2 border-[#2D2424] rounded-lg bg-white font-doodle text-base sm:text-lg text-[#7A1F2B] tracking-wider text-center break-all"
                    data-testid="loyalty-coupon-code"
                  >
                    {couponCode}
                  </code>
                  <button
                    type="button"
                    onClick={copy}
                    data-testid="loyalty-copy-btn"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded-lg hover:bg-[#5C1520]"
                  >
                    {copied ? <><Check className="w-3.5 h-3.5"/> Copied</> : <><Copy className="w-3.5 h-3.5"/> Copy</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2">
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            data-testid="loyalty-whatsapp-share"
            className="btn-whatsapp flex-1 justify-center"
          >
            <MessageCircle className="w-4 h-4"/> Share with us on WhatsApp
          </a>
          <button
            type="button"
            onClick={markSeenAndClose}
            data-testid="loyalty-later"
            className="btn-secondary flex-1"
          >
            Save for later
          </button>
        </div>
      </div>
    </div>
  );
}
